/**
 * How X counts a post's length: weighted, as twitter-text v3 does, not by
 * characters. A code point in Latin, Greek, Cyrillic, Hebrew, Arabic,
 * Devanagari and the other scripts below U+1100, plus a few punctuation
 * blocks, weighs 1; everything else (CJK, Hangul, most symbols) weighs 2;
 * an emoji weighs 2 however many code points it is built from; and every
 * link weighs 23, whatever its length. A post may weigh at most 280.
 */
import { X_COUNTRY_TLDS, X_GENERIC_TLDS } from './x-tlds.js';

export const X_MAX_WEIGHTED_LENGTH = 280;

/** What every link weighs on X, long or short. */
export const X_URL_WEIGHT = 23;

/** Code point ranges that weigh 1; the rest weigh 2 (twitter-text v3). */
const LIGHT_RANGES: ReadonlyArray<readonly [number, number]> = [
  [0, 4351],
  [8192, 8205],
  [8208, 8223],
  [8242, 8247],
];

/**
 * A web address, with or without a scheme; trailing punctuation is trimmed
 * after. Without a scheme it is a domain as twitter-text v3 reads one
 * (validDomain): labels of letters, digits and hyphens (a hyphen never
 * first), then a top-level domain checked against twitter-text's list
 * (`x-tlds.ts`) and not followed by a letter, digit, @, + or -. Nothing
 * may come right before it that twitter-text refuses there: a letter, digit
 * or underscore, @, $, # (validUrlPrecedingChars), or - . / for a domain
 * without a scheme (invalidUrlWithoutProtocolPrecedingChars).
 *
 * twitter-text v3 links a bare domain on a country code without a path too
 * (its conformance suite extracts "foo.jp", "it.so", "t.co"), so "Node.js"
 * and "Vue.js" are text only because .js is no top-level domain.
 */
const URL_PATTERN =
  /(?<![\w@$#.\-/])(?:https?:\/\/[^\s<>"]+|(?:[a-z0-9](?:[a-z0-9_-]{0,61}[a-z0-9])?\.)*[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?\.([a-z]{2,24})(?![a-z0-9@+-])(?::\d{2,5})?(?:[/?#][^\s<>"]*)?)/giu;

/** A top-level domain X links a bare address on (twitter-text v3's list). */
function isXTld(tld: string): boolean {
  return X_GENERIC_TLDS.has(tld) || X_COUNTRY_TLDS.has(tld);
}

const TRAILING_PUNCTUATION = /[)\].,!?;:'"…]+$/u;

const EMOJI = /\p{Extended_Pictographic}|\p{Regional_Indicator}|⃣/u;

const graphemes = new Intl.Segmenter('en', { granularity: 'grapheme' });

/** One piece of a post as X weighs it: a link, or one grapheme. */
export interface XSegment {
  text: string;
  weight: number;
  isUrl: boolean;
}

/** The links X would count as 23 each, at their positions in `text`. */
export function xUrls(text: string): Array<{ url: string; index: number }> {
  const found: Array<{ url: string; index: number }> = [];
  for (const match of text.matchAll(URL_PATTERN)) {
    const raw = match[0];
    const hasScheme = /^https?:\/\//i.test(raw);
    const tld = match[1]?.toLowerCase();
    if (!hasScheme && (!tld || !isXTld(tld))) continue;
    const url = raw.replace(TRAILING_PUNCTUATION, '');
    if (url.length === 0 || match.index === undefined) continue;
    found.push({ url, index: match.index });
  }
  return found;
}

function codePointWeight(codePoint: number): number {
  for (const [from, to] of LIGHT_RANGES) {
    if (codePoint >= from && codePoint <= to) return 1;
  }
  return 2;
}

/** The weight of text that holds no link. */
function plainWeight(grapheme: string): number {
  if (EMOJI.test(grapheme)) return 2;
  let weight = 0;
  for (const char of grapheme) weight += codePointWeight(char.codePointAt(0) ?? 0);
  return weight;
}

/**
 * The post cut into what X weighs: each link whole, each other grapheme on
 * its own. The text is NFC-normalised first, as X does before counting.
 */
export function xSegments(input: string): XSegment[] {
  const text = input.normalize('NFC');
  const segments: XSegment[] = [];
  let cursor = 0;
  const pushPlain = (part: string): void => {
    for (const { segment } of graphemes.segment(part)) {
      segments.push({ text: segment, weight: plainWeight(segment), isUrl: false });
    }
  };
  for (const { url, index } of xUrls(text)) {
    if (index < cursor) continue;
    pushPlain(text.slice(cursor, index));
    segments.push({ text: url, weight: X_URL_WEIGHT, isUrl: true });
    cursor = index + url.length;
  }
  pushPlain(text.slice(cursor));
  return segments;
}

/** A post's weighted length on X (at most {@link X_MAX_WEIGHTED_LENGTH}). */
export function xWeightedLength(text: string): number {
  let total = 0;
  for (const segment of xSegments(text)) total += segment.weight;
  return total;
}

/**
 * The longest start of `text` that, with `suffix` after it, weighs at most
 * `maxWeight`. Cuts between graphemes and never inside a link. Returns the
 * text unchanged when it already fits.
 */
export function xTruncate(text: string, maxWeight: number, suffix = '...'): string {
  const segments = xSegments(text);
  const total = segments.reduce((sum, s) => sum + s.weight, 0);
  if (total <= maxWeight) return text.normalize('NFC');
  const budget = maxWeight - xWeightedLength(suffix);
  let used = 0;
  let out = '';
  for (const segment of segments) {
    if (used + segment.weight > budget) break;
    used += segment.weight;
    out += segment.text;
  }
  return out + suffix;
}
